import React from "react";
import { Button } from "@mui/material";

/**
 * CustomButton - A reusable button component with primary and attachment styles
 *
 * @param {Object} props - Component props
 * @param {string} props.variant - Button variant ('primary' or 'attachment')
 * @param {string} [props.title] - Optional button text
 * @param {React.ReactNode} [props.icon] - Optional icon component
 * @param {string} [props.className] - Optional additional CSS classes
 * @param {boolean} [props.disabled] - Whether the button is disabled
 * @param {Function} [props.onClick] - Click handler function
 * @param {Object} [props.rest] - Any other MUI Button props
 */
const CustomButton = ({
  variant = "primary",
  title,
  icon,
  className = "",
  disabled = false,
  onClick,
  ...rest
}) => {
  // Determine text color based on variant
  const textColor = variant === "primary" ? "#000" : "#D8D5CD";

  // Encode SVGs as data URLs for use as background images
  const primaryBgUrl = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='210' height='53' viewBox='0 0 210 53' fill='none'%3E%3Cg clip-path='url(%23clip0_2423_9744)'%3E%3Cpath d='M0.967942 1.16929L18.6688 1.76213L18.7459 1.7644L18.8217 1.75986L36.5076 0.568115L96.2719 1.76213L96.3216 1.76289L96.3713 1.76062L110.08 1.13597L125.016 1.44867H125.064L153.801 1.13522H208.428V13.2177L207.817 27.0393L207.815 27.0621L207.818 27.0848L209.065 41.4826L208.462 51.4542H193.417L178.337 51.7722H178.321L178.306 51.7729L162.39 52.4316L147.414 52.0909H147.394L81.5992 51.4542H81.5582L53.7488 51.9425L38.8375 51.4549L38.7555 51.4519L38.6735 51.458L25.8623 52.4294L0.930664 51.489V27.0719L1.90859 17.017L1.91108 16.9905L1.90859 16.9633L0.967942 1.16929Z' stroke='%2343400B' stroke-width='1.8638'/%3E%3Cpath d='M1.60443 1.94173L19.1974 2.51649L19.274 2.51869L19.3493 2.51429L36.9274 1.35889L96.3272 2.51649L96.3766 2.51723L96.426 2.51502L110.051 1.90943L124.896 2.21259H124.944L153.505 1.90869H207.799V13.6227L207.192 27.0229L207.191 27.0449L207.193 27.067L208.433 41.0258L207.833 50.6933H192.88L177.892 51.0016H177.876L177.861 51.0023L162.042 51.6409L147.157 51.3106H147.137L81.744 50.6933H81.7033L54.0634 51.1667L39.2431 50.694L39.1616 50.6911L39.0801 50.6969L26.347 51.6387L1.56738 50.727V27.0545L2.53935 17.3062L2.54182 17.2805L2.53935 17.2541L1.60443 1.94173Z' fill='%23D8D5CD'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_2423_9744'%3E%3Crect width='210' height='53' rx='2' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E")`;

  const attachmentBgUrl = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='248' height='53' viewBox='0 0 248 53' fill='none'%3E%3Cg clip-path='url(%23clip0_2423_9969)'%3E%3Cpath d='M1.14266 1.16904L22.0466 1.76189L22.1375 1.76416L22.2271 1.75961L43.1134 0.567871L113.692 1.76189L113.751 1.76264L113.809 1.76037L129.998 1.13573L147.637 1.44843H147.695L181.631 1.13497H246.143V13.2175L245.421 27.0391L245.42 27.0618L245.423 27.0845L246.896 41.4824L246.183 51.4539H228.416L210.607 51.7719H210.588L210.571 51.7727L191.774 52.4314L174.088 52.0907H174.065L96.3644 51.4539H96.316L63.4743 51.9423L45.8648 51.4547L45.768 51.4517L45.6711 51.4577L30.5417 52.4291L1.09863 51.4888V27.0717L2.25352 17.0168L2.25646 16.9903L2.25352 16.963L1.14266 1.16904Z' stroke='%23D8D5CD' stroke-width='1.8638'/%3E%3Cpath d='M1.89434 1.94173L22.6708 2.51649L22.7612 2.51869L22.8502 2.51429L43.6091 1.35889L113.757 2.51649L113.816 2.51723L113.874 2.51502L129.964 1.90943L147.496 2.21259H147.553L181.282 1.90869H245.401V13.6227L244.683 27.0229L244.682 27.0449L244.685 27.067L246.149 41.0258L245.44 50.6933H227.782L210.082 51.0016H210.063L210.045 51.0023L191.363 51.6409L173.785 51.3106H173.762L96.5354 50.6933H96.4873L63.8459 51.1667L46.3438 50.694L46.2476 50.6911L46.1513 50.6969L31.1141 51.6387L1.85059 50.727V27.0545L2.99843 17.3062L3.00135 17.2805L2.99843 17.2541L1.89434 1.94173Z' fill='%23474747'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_2423_9969'%3E%3Crect width='248' height='53' rx='2' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E")`;

  // Set button styles
  const buttonStyles = {
    width: variant === "primary" ? 210 : 248,
    height: 53,
    backgroundImage: variant === "primary" ? primaryBgUrl : attachmentBgUrl,
    backgroundSize: "100% 100%",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    color: textColor,
    textTransform: "none",
    padding: "0 16px",
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 2,
    "&:hover": {
      backgroundImage: variant === "primary" ? primaryBgUrl : attachmentBgUrl,
      opacity: 0.9,
    },
    "&.Mui-disabled": {
      backgroundImage: variant === "primary" ? primaryBgUrl : attachmentBgUrl,
      opacity: 0.5,
    },
  };

  return (
    <Button
      className={className}
      disabled={disabled}
      onClick={onClick}
      sx={buttonStyles}
      {...rest}
    >
      {icon && (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            marginRight: title ? "8px" : 0,
          }}
        >
          {icon}
        </span>
      )}
      {title && <span>{title}</span>}
    </Button>
  );
};

export default CustomButton;
